"use client";

import { useResolveHref } from "@/lib/links";
import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "@/components/ui/Button";
import { ArrowRight, ChevronDown } from "@/components/ui/icons";
import { LINKS } from "@/content/quikit";
import { cn } from "@/lib/utils";
import { LEFT_LINKS, MENUS, RIGHT_LINKS } from "./nav-data";

const EASE_OUT = [0.23, 1, 0.32, 1] as const;
const itemVariants = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE_OUT } },
};

export function MobileMenu({ open, onClose }: { open: boolean; onClose: () => void }) {
  const r = useResolveHref();
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="mobile-menu"
          data-lenis-prevent=""
          initial={{ clipPath: "inset(0% 0% 100% 0%)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
          exit={{ clipPath: "inset(0% 0% 100% 0%)", transition: { duration: 0.35, ease: [0.77, 0, 0.175, 1] } }}
          transition={{ duration: 0.6, ease: EASE_OUT }}
          className="fixed inset-x-0 bottom-0 top-[var(--nav-h)] z-40 overflow-y-auto bg-white px-[var(--pad)] pb-10 pt-4 text-ink lg:hidden"
        >
          <motion.ul
            initial="hidden"
            animate="show"
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.05, delayChildren: 0.12 } } }}
            className="divide-y divide-line"
          >
            {MENUS.map((menu) => {
              const isOpen = expanded === menu.id;
              return (
                <motion.li key={menu.id} variants={itemVariants} className="py-2">
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : menu.id)}
                    aria-expanded={isOpen}
                    className="flex w-full items-center justify-between py-3 text-[26px] font-medium tracking-[-0.035em]"
                  >
                    {menu.label}
                    <ChevronDown className={cn("size-5 transition-transform duration-300", isOpen && "rotate-180")} />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: EASE_OUT }}
                        className="overflow-hidden"
                      >
                        {/* The same headline the desktop mega menu carries. Without it the apps
                            index has no route in from the nav on a phone at all — the list below
                            reaches individual apps only. */}
                        <a
                          href={r(menu.headline.href)}
                          onClick={onClose}
                          className="group mt-1 inline-flex items-center gap-2 text-[15px] font-medium tracking-[-0.02em]"
                        >
                          {menu.headline.label}
                          <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </a>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-5 pb-4 pt-5">
                          {menu.groups.map((group) => (
                            <div key={group.title}>
                              <p className="text-[14px] font-medium">{group.title}</p>
                              <ul className="mt-2 space-y-1.5">
                                {group.links.map((link) => (
                                  <li key={link.label}>
                                    <a href={r(link.href)} onClick={onClose} className="text-[14px] text-muted">
                                      {link.label}
                                      {link.soon && <span className="ml-1 text-[10px] text-faint">Soon</span>}
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.li>
              );
            })}
            {[...LEFT_LINKS, ...RIGHT_LINKS].map((link) => (
              <motion.li key={link.label} variants={itemVariants} className="py-2">
                <a href={r(link.href)} onClick={onClose} className="block py-3 text-[26px] font-medium tracking-[-0.035em]">
                  {link.label}
                </a>
              </motion.li>
            ))}
          </motion.ul>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0, transition: { delay: 0.35, duration: 0.5, ease: EASE_OUT } }}
            className="mt-8 grid grid-cols-2 gap-3"
          >
            <Button variant="outline" size="lg" href={LINKS.login} onClick={onClose}>
              Log in
            </Button>
            <Button variant="mint" size="lg" href={LINKS.register} onClick={onClose}>
              Start free
            </Button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
